import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ErrorService, LayoutService } from '@core/services';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { 
  EmployeeWorkSchedule, 
  EmployeeWorkScheduleDialog, 
  EmployeeWorkScheduleDialogForm, 
  EmployeeWorkSchedulePost, 
  ScheduleNonWorkdays, 
  WorkScheduleDialogMode} from '@core/models/leave-admin/employees/employee-schedules.model';
import { ManageEmployeesService } from '@core/services/manage-employees.service';
import { EmployeeRecordStoreService } from '@core/services/employee-record-store.service';
import { CaseDetails, SelectionOption, StiiraError } from '@core/models';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';
import { EmployeeRecord } from '@core/models/leave-admin/employees/employee-record.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { DayOfWeek } from '@core/enums';
import { isNumber } from '@microsoft/applicationinsights-core-js';
import { Router } from '@angular/router';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';

@Component({
    selector: 'app-add-edit-employee-schedule',
    templateUrl: './add-edit-employee-schedule.component.html',
    styleUrl: './add-edit-employee-schedule.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddEditEmployeeScheduleComponent implements OnInit{
  static readonly componentName = 'AddEditEmployeeScheduleComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  
  public form: FormGroup<EmployeeWorkScheduleDialogForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public isMobile$: Observable<boolean>;
  public formChangeEmitted: boolean = false;
  public scheduleNonWorkdays: ScheduleNonWorkdays;
  public dayOfWeekOptions: SelectionOption[];
  
  public position = position;
  public onDragEnded = onDragEnded;

  private destroy$: Subject<void> = new Subject<void>();
  private dayOfWeek = DayOfWeek;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      sysText: any,
      unsavedChangesSysText: any,
      updateConfSysText: any,
      mode: WorkScheduleDialogMode,
      employeeId: number, //nullable
      caseId: number, //nullable
      isAnonymousCase: boolean, //nullable
      employeeSchedule: EmployeeWorkSchedule, //nullable
      dialogData: EmployeeWorkScheduleDialog
    },
    private manageEmployeeService: ManageEmployeesService,
    private caseDetailsService: LeaveAdminService,
    private employeeRecordStore: EmployeeRecordStoreService,
    private caseDetailsStore: LeaveAdminStoreService,
    private errorService: ErrorService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AddEditEmployeeScheduleComponent>,
    private router: Router,
    private layoutService: LayoutService
  ) {
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group<EmployeeWorkScheduleDialogForm>({
      effectiveDate: this.fb.control(null),
      employerWorkScheduleId: this.fb.control(null),
      isIrregular: this.fb.control(false),
      nonWorkdays: this.fb.control([]),
      averageWeeklyHours: this.fb.control(null),
      averageWeeklyWorkdays: this.fb.control(null),
      startOfWorkweek: this.fb.control(null)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    this.dayOfWeekOptions = this.buildDayOfWeekOptions();
    this.form.controls.averageWeeklyHours.disable();
    this.form.controls.averageWeeklyWorkdays.disable();
    this.form.controls.startOfWorkweek.disable();

    if (this.data.employeeSchedule) {
      this.form.patchValue({
        effectiveDate: this.data.employeeSchedule.effectiveDate,
        employerWorkScheduleId: this.data.employeeSchedule.employerWorkScheduleId,
        isIrregular: this.data.employeeSchedule.isIrregular,
        nonWorkdays: this.data.employeeSchedule.scheduleNonWorkdays.map(snwd => snwd.id as number),
        averageWeeklyHours: this.data.employeeSchedule.averageWeeklyHours,
        averageWeeklyWorkdays: this.data.employeeSchedule.averageWeeklyWorkdays,
        startOfWorkweek: this.data.employeeSchedule.startOfWorkweek
      });

      this.scheduleNonWorkdays = this.data.dialogData.scheduleNonWorkdays
        .find(snwd => snwd.scheduleId === this.data.employeeSchedule.employerWorkScheduleId);

      if (this.data.employeeSchedule.isIrregular) {
        this.form.controls.employerWorkScheduleId.disable();
        this.form.controls.averageWeeklyHours.enable();
        this.form.controls.averageWeeklyWorkdays.enable();
        this.form.controls.startOfWorkweek.enable();
      }
    }

    this.form.controls.employerWorkScheduleId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.scheduleNonWorkdays = this.data.dialogData.scheduleNonWorkdays
          .find(snwd => snwd.scheduleId === res);
        if (this.scheduleNonWorkdays) {
          const valsNoDups = [...new Set([...this.form.controls.nonWorkdays.value, ...this.scheduleNonWorkdays.nonWorkdays])];
          this.form.controls.nonWorkdays.patchValue(valsNoDups);
        } else {
          this.scheduleNonWorkdays = null;
        }
      });

    this.form.controls.isIrregular.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.handleIrregularToggle(res);
      });

    this.setFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setFormErrors() {
    setTimeout(() => {
      this.formInitValues = { ...this.form.value }
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  public onSubmit(overrideUpdateConf: boolean = false): void {
    if (this.data.caseId && this.caseDetailsStore.isActionRunning) {
      this.openPendingChangesDialog(overrideUpdateConf);
      return;
    }

    const dto: EmployeeWorkSchedulePost = {
      employeeId: this.data.employeeId,
      overrideUpdateConf: overrideUpdateConf,
      employeeWorkScheduleId: this.data.employeeSchedule?.employeeWorkScheduleId ?? null,
      effectiveDate: this.form.controls.effectiveDate.value,
      employerWorkScheduleId: this.form.controls.employerWorkScheduleId.value,

      isIrregular: this.form.controls.isIrregular.value,
      averageWeeklyHours: this.form.controls.isIrregular.value ? 
        this.form.controls.averageWeeklyHours.value : null,
      averageWeeklyWorkdays: this.form.controls.isIrregular.value ? 
        this.form.controls.averageWeeklyWorkdays.value : null,  
      startOfWorkweek: this.form.controls.isIrregular.value ? 
        this.form.controls.startOfWorkweek.value : null,

      nonWorkdays: this.form.controls.nonWorkdays.value
    }

    if (this.data.mode == WorkScheduleDialogMode.employeeRecord) {
      const postObs = this.data.employeeSchedule
        ? this.manageEmployeeService.postEditEmployeeWorkSchedule(dto)
        : this.manageEmployeeService.postNewEmployeeWorkSchedule(dto);

      postObs.pipe(
        indicate(this.isSubmitting$),
        finalize(() => { }))
        .subscribe((res) => {
          if (res == null) {
            var currentUrl = this.router.url;
            this.isEditing.emit(false);
            this.dialogRef.close();
            this.router.navigateByUrl('/home', { skipLocationChange: true }).then(() => { this.router.navigate([currentUrl]) });
          } else if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
          } else {
            this.employeeRecordStore.employeeRecord = res as EmployeeRecord;
            this.dialogRef.close();
          }
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
    } 
    
    else if (this.data.mode == WorkScheduleDialogMode.caseDetails) {
      dto.caseId = this.data.caseId;
      const postObs = this.data.employeeSchedule
        ? this.caseDetailsService.postEditEmployeeWorkSchedule(dto)
        : this.caseDetailsService.postNewEmployeeWorkSchedule(dto);

      postObs.pipe(
        indicate(this.isSubmitting$),
        finalize(() => { }))
        .subscribe((res) => {
          if (res == null) {
            var currentUrl = this.router.url;
            this.isEditing.emit(false);
            this.dialogRef.close();
            this.router.navigateByUrl('/home', { skipLocationChange: true }).then(() => { this.router.navigate([currentUrl]) });
          } else if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
          } else {
            this.caseDetailsStore.caseDetails = res as CaseDetails;
            this.dialogRef.close();
          }
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
    }
  }

  public cancel(): void {
    if (this.noChanges) {
      this.dialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };

    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private openUpdateConfirmationDialog(updateConfirmationDialog: UpdateConfirmationDialog): void {
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.data.updateConfSysText,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true);
        }
      });
  }

  private openPendingChangesDialog(overrideUpdateConf: boolean): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit(overrideUpdateConf);
      }
    });
  }

  private buildDayOfWeekOptions(): SelectionOption[] {
    const dayOfWeekIds = Object.values(this.dayOfWeek).filter(v => isNumber(v));
    let dayOfWeekOptions: SelectionOption[] = [];

    dayOfWeekIds.forEach(id => {
      const desc: string = this.dayOfWeek[id];
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      dayOfWeekOptions.push(option);
    });

    return dayOfWeekOptions;
  }

  private handleIrregularToggle(res:boolean): void {
    if (res) {
      this.form.controls.employerWorkScheduleId.setValue(null, {emitEvent: false});
      this.form.controls.employerWorkScheduleId.disable();

      this.form.controls.averageWeeklyHours.enable();
      this.form.controls.averageWeeklyWorkdays.enable();
      this.form.controls.startOfWorkweek.enable();

      if (this.data.employeeSchedule?.averageWeeklyHours) {
        this.form.controls.averageWeeklyHours
          .setValue(this.data.employeeSchedule.averageWeeklyHours)
      }
      if (this.data.employeeSchedule?.averageWeeklyWorkdays) {
        this.form.controls.averageWeeklyWorkdays
          .setValue(this.data.employeeSchedule.averageWeeklyWorkdays)
      }
      if (this.data.employeeSchedule?.startOfWorkweek != null) {
        this.form.controls.startOfWorkweek
          .setValue(this.data.employeeSchedule.startOfWorkweek)
      }
    } else {
      this.form.controls.employerWorkScheduleId.enable();

      this.form.controls.averageWeeklyHours.disable();
      this.form.controls.averageWeeklyHours.setValue(null);

      this.form.controls.averageWeeklyWorkdays.disable();
      this.form.controls.averageWeeklyWorkdays.setValue(null);

      this.form.controls.startOfWorkweek.disable();
      this.form.controls.startOfWorkweek.setValue(null);
    }
  }
}